<script setup lang="ts">
import { computed, markRaw, ref, watch } from 'vue'

import IconSettingGallery from '@/components/atoms/icons/IconSettingGallery.vue'
import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import SettingGalleryItemsPerLine from '@/components/atoms/SettingGalleryItemsPerLine.vue'
import SettingGalleryType from '@/components/beta-editor/settings/controls/BetaSettingGalleryType.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingText from '@/components/atoms/SettingText.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useMediaManagerStore } from '@/stores/modal/media-manager'

const BETA_GALLERY_MEDIA_SELECTION_LIMIT = Number.MAX_SAFE_INTEGER

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const mediaManagerStore = useMediaManagerStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const galleryItems = computed(() => elementForEdit.value?.frameContent?.items ?? [])
const galleryText = computed(() => {
    const value = elementForEdit.value?.frameContent?.text

    return typeof value === 'string' ? value.replace(/<[^>]*>/g, '') : ''
})
const selectedItemIndex = ref(0)
const itemsSetting = {
    id: 1,
    name: 'items',
    icon: markRaw(IconSettingGallery),
}
const layoutSetting = {
    id: 2,
    name: 'layout',
    icon: markRaw(IconSettingLayout),
}

const selectedSectionIndex = computed(() => {
    const schemaSectionIndex = Array.isArray(elementForEdit.value?.schema)
        ? elementForEdit.value.schema[0]?.index
        : null

    return elementForEdit.value?.sectionIndex ?? schemaSectionIndex
})
const selectedSection = computed(() => {
    const index = selectedSectionIndex.value

    if (index === null || index === undefined) return null

    return editorPageStore.activePage?.content?.[index] ?? null
})
const selectedSectionComponent = computed(() => String(selectedSection.value?.component ?? '').toLowerCase())

const findFirstGalleryElement = (elements: any[]): any | null => {
    if (!Array.isArray(elements)) return null

    for (const element of elements) {
        if (!element) continue

        if (element.frameType === 'gallery') return element

        if (Array.isArray(element.frameContent)) {
            const nestedGallery = findFirstGalleryElement(element.frameContent)

            if (nestedGallery) return nestedGallery
        }
    }

    return null
}
const firstGalleryElement = computed(() => {
    const sections = editorPageStore.activePage?.content ?? []

    for (const section of sections) {
        const gallery = findFirstGalleryElement(section?.frameContent ?? [])

        if (gallery) return gallery
    }

    return null
})
const selectedGalleryId = computed(() => betaStore.getElementId(elementForEdit.value))
const firstGalleryId = computed(() => betaStore.getElementId(firstGalleryElement.value))
const isSelectedFirstHomeGallery = computed(() => (
    Boolean(editorPageStore.activePage?.isHome)
    && elementForEdit.value?.frameType === 'gallery'
    && selectedGalleryId.value != null
    && selectedGalleryId.value === firstGalleryId.value
))
const isSelectedSectionImported = computed(() => Boolean(selectedSection.value?.frameOrigin))
const isSelectedSectionLayoutEditable = computed(() => (
    selectedSectionComponent.value.includes('maincontent')
    || selectedSectionComponent.value.includes('post')
))
const canConfigureGalleryLayout = computed(() => (
    isSelectedSectionLayoutEditable.value
    && !isSelectedSectionImported.value
    && !isSelectedFirstHomeGallery.value
))
const settings = computed(() => (
    canConfigureGalleryLayout.value
        ? [itemsSetting, layoutSetting]
        : [itemsSetting]
))

watch(
    () => galleryItems.value.length,
    length => {
        if (length === 0) {
            selectedItemIndex.value = 0
            return
        }

        if (selectedItemIndex.value > length - 1) {
            selectedItemIndex.value = length - 1
        }
    },
    { immediate: true },
)

const updateGalleryItems = (items: any[]) => {
    if (!elementForEdit.value) return

    elementForEdit.value.frameContent = {
        ...elementForEdit.value.frameContent,
        items,
    }

    betaStore.updateElementForEdit()
}

const onChangeGalleryText = (event: any) => {
    if (!elementForEdit.value || !isSelectedFirstHomeGallery.value) return

    const value = typeof event?.value === 'string' ? event.value : ''
    const hasText = value.trim().length > 0

    elementForEdit.value.frameContent = {
        ...(elementForEdit.value.frameContent ?? {}),
        text: hasText ? value : '',
        showText: hasText ? '1' : '0',
    }

    betaStore.updateElementForEdit()
}

const onAddMediaInGallery = () => {
    mediaManagerStore.setMaxSelectedMedias(BETA_GALLERY_MEDIA_SELECTION_LIMIT)
    mediaManagerStore.setIsOpenModal(true)
    mediaManagerStore.setAction((medias: any[]) => {
        const newItems = medias.map((media: any) => ({
            ...defaultStore.newElementGalleryItem,
            type: media.type,
            image: {
                url: media.content,
                width: media.width,
                height: media.height,
                alt: media.name,
                createdAt: media.datetime,
            },
            embed: media.embed ?? null,
        }))

        updateGalleryItems([...galleryItems.value, ...newItems])
    })
}

const removeGalleryItem = (index: number) => {
    const nextItems = [...galleryItems.value]
    nextItems.splice(index, 1)
    updateGalleryItems(nextItems)
}

const moveGalleryItem = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    if (targetIndex < 0 || targetIndex >= galleryItems.value.length) return

    const nextItems = [...galleryItems.value]
    const [item] = nextItems.splice(index, 1)
    nextItems.splice(targetIndex, 0, item)
    updateGalleryItems(nextItems)
    selectedItemIndex.value = targetIndex
}

const onChangeGalleryType = (event: any) => {
    if (!elementForEdit.value) return

    if (event.value.id === 0) {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            maincontent_gallery_id: event.value.id,
            maincontent_gallery: event.value.variables.maincontent_gallery,
            maincontent_gallery_items: '',
            maincontent_gallery_items_mobile: '',
            maincontent_gallery_items_crop: '',
            maincontent_gallery_padding: '',
        }
    } else {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            maincontent_gallery_id: event.value.id,
            maincontent_gallery: event.value.variables.maincontent_gallery,
            maincontent_gallery_items: event.value.variables.maincontent_gallery_items,
            maincontent_gallery_items_mobile: event.value.variables.maincontent_gallery_items_mobile,
            maincontent_gallery_items_crop: event.value.variables.maincontent_gallery_items_crop,
            maincontent_gallery_padding: event.value.variables.maincontent_gallery_padding,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeGalleryItemsPerLine = (event: any) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        maincontent_gallery_items: event.value,
    }

    betaStore.updateElementForEdit()
}

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeSpacing = (event: any, target: 'margin' | 'padding') => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [target]: {
                ...elementForEdit.value.extra?.[target],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile`]: {
                ...elementForEdit.value.extra?.[`${target}_mobile`],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile_lock`]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #items>
            <settings-layout
                v-if="isSelectedFirstHomeGallery"
                name="Frase da Galeria"
                :default-open="true"
            >
                <setting-text
                    class="gallery-text-setting"
                    label="Frase"
                    placeholder="Digite uma frase ou deixe em branco para removê-la"
                    :value="galleryText"
                    @change="onChangeGalleryText"
                />
            </settings-layout>

            <settings-layout name="Itens da Galeria" :default-open="true">
                <button class="gallery-action" @click="onAddMediaInGallery">
                    Adicionar mídia
                </button>

                <div v-if="galleryItems.length === 0" class="gallery-empty-state">
                    Nenhum item adicionado ainda.
                </div>

                <div v-else class="gallery-empty-state">
                    {{ galleryItems.length }} {{ galleryItems.length === 1 ? 'item adicionado' : 'itens adicionados' }}.
                    Edite, troque e exclua as mídias direto no painel da galeria.
                </div>
            </settings-layout>
        </template>

        <template v-if="canConfigureGalleryLayout" #layout>
            <settings-layout name="Layout da Galeria" :default-open="true">
                <setting-gallery-type
                    label="Design personalizado da galeria"
                    :value="elementForEdit.extra?.maincontent_gallery_id"
                    @change="onChangeGalleryType"
                />

                <setting-gallery-items-per-line
                    label="Itens por linha (Desktop)"
                    :value="elementForEdit.extra?.maincontent_gallery_items"
                    @change="onChangeGalleryItemsPerLine"
                />
            </settings-layout>

            <settings-layout name="Configurações do Conteúdo" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="[4, 6, 12]"
                    :values-permission-mobile="[4, 6, 12]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'margin')"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'padding')"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>

<style scoped>
.gallery-action {
    @apply w-full rounded-full border border-dashed border-[#BFD2EC] bg-white px-4 py-3 text-sm font-semibold text-[#315A9A];
}

.gallery-empty-state {
    @apply rounded-2xl border border-dashed border-[#DCE5F2] bg-[#F9FBFF] px-4 py-4 text-sm text-[#6C7E99];
}

</style>
